import { Spinner } from '@frontend/components/common';
import { IProduct, ProductType } from '@frontend/product/types';
import { Product } from '@frontend/product/utils';
import { useBlobRepository } from '@frontend/repository';
import { ReactNode, useEffect, useState } from 'react';

import { useAppSelector } from '../redux/store';
import { useKioskNavigate } from '../utils/kiosk-navigation';

export interface StockItemProps {
    product: {
        id: string;
        name: ReactNode;
        type?: ProductType;
    };
    attributes?: Record<string, string> | null;
    quantity: number;
    isLast: boolean;
    allowNavigate?: boolean;
}

const BADGE_CLASS = 'bg-border-default flex h-6 min-w-6 items-center justify-center gap-2.5 rounded px-1';
const BADGE_TEXT_CLASS = 'text-foreground-default justify-start text-sm font-medium';

export const StockItemComponent = (props: StockItemProps) => {
    const navigate = useKioskNavigate();
    const blobRepository = useBlobRepository();
    const navigationState = useAppSelector((store) => store.navigation);
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const attributeEntries = props.attributes ? Object.entries(props.attributes) : [];
    const hasAttributes = attributeEntries.length > 0;
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [image, setImage] = useState<string | null>(null);

    useEffect(() => {
        if (!props.product.type) return;
        if (!accountId) return;
        setIsLoading(true);
        new Product({ ...(props.product as IProduct), account_id: accountId }).getBlobImage(blobRepository).then((img) => {
            setImage(img);
            setIsLoading(false);
        });
    }, [accountId, props.product.id, props.product.type]);
    return (
        <div
            onClick={() => props.allowNavigate && navigate(`./${props.product.id}`)}
            className={`border-border-default bg-background-surface flex w-full items-start justify-start gap-2 rounded-xl p-3 py-3 ${!props.isLast ? 'border-b' : ''}`}>
            {props.product.type && (
                <div className='text-background-surface flex w-20 items-center justify-center self-stretch rounded text-sm font-semibold'>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <img
                            src={image ?? ''}
                            alt={props.product.name as string}
                            className='rounded object-cover'
                        />
                    )}
                </div>
            )}
            <div className='flex flex-1 flex-col items-start justify-start gap-1'>
                <div className='text-foreground-default justify-start text-base font-medium'>{props.product.name}</div>
                <div className='flex flex-wrap items-center justify-start gap-1'>
                    {hasAttributes ? (
                        attributeEntries.map(([key, value]) => (
                            <div
                                key={key}
                                className={BADGE_CLASS}>
                                <span className={BADGE_TEXT_CLASS}>
                                    {key}: {value}
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className='h-6' />
                    )}
                </div>
            </div>
            <div className='bg-brand-muted flex items-start justify-center gap-1 rounded-full px-2 py-1'>
                <span className='text-background-surface justify-start text-center text-sm font-semibold'>{props.quantity}x</span>
            </div>
        </div>
    );
};
